import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import * as workAPI from '@App/api/routes/work'
import RootStore from '@App/stores/root-store'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import Navigation from '@App/navigation'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'
import { CREATE_WORK_ERROR_MESSAGE } from '@Const/error-messages'
import { PaginationInfo } from '@Entities/pagination'
import { WorkModel } from '@Entities/work/work'
import { setItemToArrayByIndex } from '@App/helpers/work-entities/removers'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { isOverclaimAllowed } from '@App/helpers/is-overclaim-allowed'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'
import { RecordingModel } from '@Entities/work/recording-model'
import numberOrZero from '@App/helpers/number-or-zero'

const enum WorkAction {
    draftCreated,
    workCreated,
    draftUpdated,
    editWorkCreated,
    editDraftCreated,
}

export default class WorkStore {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<
            WorkStore,
            | '_worksHistoryRightsHolderPaginationInfo'
            | '_wizardStepIsValid'
            | '_createdWorkId'
            | '_workById'
            | '_workDraftLocal'
            | 'cleanPropertiesAndReturn'
            | 'setRedirectTrigger'
            | '_overclaimWarningOpen'
        >(this, {
            _worksHistoryRightsHolderPaginationInfo: observable,
            _wizardStepIsValid: observable,
            _createdWorkId: observable,
            _workDraftLocal: observable,
            _workById: observable,
            _overclaimWarningOpen: observable,
            isOverclaimAllowed: computed,
            sharesClaimedInWizard: computed,
            createWriter: action,
            cleanPropertiesAndReturn: action,
            setWizardStepValidation: action,
            setCreatedWorkId: action,
            setWorkDraftToLocal: action,
            destroyViewWork: action,
            destroyDraftWork: action,
            createWorkFromNavBar: action,
            setRedirectTrigger: action,
            setFoundOriginalPublisherToDraft: action,
            setOverclaimWarningOpen: action,
        })

        this.rootStore = rootStore
    }

    private _worksHistoryRightsHolderPaginationInfo: PaginationInfo | undefined

    private _wizardStepIsValid: boolean | undefined

    private _createdWorkId: number | undefined

    private _workDraftLocal: WorkModel | undefined

    private _workById: WorkModel | undefined

    private _redirectingToClaimingTool = false

    private _overclaimWarningOpen = false

    private _originalSubmissionId?: number

    setOriginalSubmissionId(submissionId?: number) {
        this._originalSubmissionId = submissionId
    }

    async getWorkById(id: number, typeGetting: FetchType): Promise<void> {
        try {
            const response = await workAPI.getWorkById(
                id,
                typeGetting,
                this.rootStore.rightsholderStore.currentRightsholderId
            )
            if (!response) {
                return
            }
            const workWithLocalId: WorkModel = WorkStore.mapResponseToWorkObject(response.data)
            runInAction(() => (this._workById = workWithLocalId))
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    async createWriter(writer: WriterModel): Promise<WriterModel> {
        try {
            const response = await workAPI.createWriter(writer)
            return response.data
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
            throw e
        }
    }

    private cleanPropertiesAndReturn(data: WorkModel): WorkModel {
        data.recordings = data.recordings.filter((aRecording) => !('amend' in aRecording) || aRecording.amend === true)
        data.alternativeTitles = data.alternativeTitles.filter(
            (anAltTitle) => !('amend' in anAltTitle) || anAltTitle.amend === true
        )
        data.originalPublishers = data.originalPublishers.filter(
            (aPublisher) => !('amend' in aPublisher) || aPublisher.amend === true
        )
        return data
    }

    async createWork(data: WorkModel, rightsHolderId: number, isDraft = false): Promise<any> {
        try {
            if (!isDraft) {
                data = this.cleanPropertiesAndReturn(data)
            }
            const response = await workAPI.postWork(data, rightsHolderId, this._originalSubmissionId)
            this._originalSubmissionId = undefined
            return response.data
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    async deleteDraftRightsHolder(draftId: number): Promise<void> {
        try {
            await workAPI.deleteDraftRightsHolder(draftId)
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    async deleteAllDraftsRightsHolder(rightsHolderId: number): Promise<void> {
        try {
            await workAPI.deleteAllDraftsRightsHolder(rightsHolderId)
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    setWizardStepValidation(isValid: boolean): void {
        this._wizardStepIsValid = isValid
    }

    setCreatedWorkId(workId: number | undefined): void {
        this._createdWorkId = workId
    }

    setWorkDraftToLocal(data: WorkModel): void {
        this._workDraftLocal = data
    }

    setOverclaimWarningOpen = (isOpen: boolean): void => {
        this._overclaimWarningOpen = isOpen
    }

    get workById(): WorkModel | undefined {
        return this._workById
    }

    get wizardStepIsValid(): boolean | undefined {
        return this._wizardStepIsValid
    }

    get createdWorkId(): number | undefined {
        return this._createdWorkId
    }

    get workDraftLocal(): WorkModel | undefined {
        return this._workDraftLocal
    }

    get overclaimWarningOpen(): boolean {
        return this._overclaimWarningOpen
    }

    isWorkDraftOverclaim(): boolean {
        return this.sharesClaimedInWizard >= 100.06
    }

    get sharesClaimedInWizard(): number {
        return (
            this.workDraftLocal?.originalPublishers
                .map((op) => op.publisherShare ?? 0)
                .concat(
                    this.workDraftLocal?.originalPublishers.flatMap((op) =>
                        op.administratorPublishers.map((ap) => numberOrZero(ap.publisherShare))
                    )
                )
                .reduce((partialSum, a) => partialSum + a, 0) ?? 0
        )
    }

    get isOverclaimAllowed() {
        return isOverclaimAllowed(this._workDraftLocal)
    }

    onNextValidateOverclaim = () => {
        if (this.isWorkDraftOverclaim() && this.isOverclaimAllowed) {
            this.setOverclaimWarningOpen(true)
            return false
        }
        return true
    }

    destroyViewWork() {
        if (!this.redirectingToClaimingTool) {
            this._workById = undefined
        }
        this.setRedirectingToClaimingTool(false)
    }

    destroyDraftWork(): void {
        this._workDraftLocal = undefined
    }

    public static mapResponseToWorkObject(response: WorkModel): WorkModel {
        return {
            ...response,
            alternativeTitles: response.alternativeTitles
                ? response.alternativeTitles.map((data: AlternativeTitleModel, indexAT: number) => ({
                      ...data,
                      localId: indexAT + 1,
                  }))
                : [],
            originalPublishers: response.originalPublishers
                ? response.originalPublishers.map((op: OriginalPublisherModel, indexOP: number) => ({
                      ...op,
                      localId: indexOP + 1,
                      administratorPublishers: op.administratorPublishers
                          ? op.administratorPublishers.map((ap: PublisherModel, indexAP: number) => ({
                                ...ap,
                                localId: indexAP + 1,
                            }))
                          : [],
                      writers: op.writers
                          ? op.writers.map((writer: WriterModel, indexWR: number) => ({
                                ...writer,
                                localId: indexWR + 1,
                            }))
                          : [],
                  }))
                : [],
            writers: response.writers
                ? response.writers.map((writer: WriterModel, indexWR: number) => ({
                      ...writer,
                      localId: indexWR + 1,
                  }))
                : [],
            recordings: response.recordings
                ? response.recordings.map((recording: RecordingModel, indexRES: number) => ({
                      ...recording,
                      localId: indexRES + 1,
                  }))
                : [],
        }
    }

    async createWorkFromNavBar(data: WorkModel, rhId: number, isDraft = false, isSavingDraft = false) {
        try {
            const workDraftFromLocal = this.workDraftLocal

            if (!workDraftFromLocal) {
                return
            }
            const response = await this.createWork(data, rhId, isDraft)

            if (isSavingDraft) {
                this.setRedirectTrigger(WorkAction.draftCreated, response)
            } else if (workDraftFromLocal.id && workDraftFromLocal.isComplete) {
                this.setRedirectTrigger(WorkAction.editWorkCreated, response) // Edit work to work
            } else if (workDraftFromLocal.id) {
                this.setRedirectTrigger(WorkAction.editDraftCreated, response) // Edit draft to work
            } else {
                // Move to submission step
                this.setCreatedWorkId(response.id)
                this.setWorkDraftToLocal(response)
                this.setRedirectTrigger(WorkAction.workCreated, response) // Move to submission step
            }
        } catch (ignore) {
            this.rootStore.popupStore.setAlertData(CREATE_WORK_ERROR_MESSAGE, true)
        }
    }

    public setFoundOriginalPublisherToDraft = (publisher: OriginalPublisherModel) => {
        const workDraftFromLocal = this._workDraftLocal

        if (!workDraftFromLocal) {
            return
        }

        const originalPublishers = workDraftFromLocal.originalPublishers
        const lastOPId = originalPublishers.length
            ? Number(originalPublishers[originalPublishers.length - 1].localId)
            : 0

        const newOriginalPublisher: OriginalPublisherModel = {
            localId: lastOPId + 1,
            ipId: publisher.ipId,
            amend: true,
            publisherName: publisher.publisherName,
            ipiNumber: publisher.ipiNumber ? publisher.ipiNumber : null,
            hfaPublisherNumber: publisher.hfaPublisherNumber,
            publisherShare: 0,
            lastUpdated: publisher.lastUpdated,
            administratorPublishers: [],
            writers: [], // HARD CODE
        }

        const updatedPublisherDraft: WorkModel = {
            ...workDraftFromLocal,
            originalPublishers: [...originalPublishers, newOriginalPublisher],
        }

        this.setWorkDraftToLocal(updatedPublisherDraft)
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    public setFoundAdministratorPublisher = (publisher: PublisherModel, currentOP: OriginalPublisherModel) => {
        const workDraftFromLocal = this._workDraftLocal

        if (!workDraftFromLocal) {
            return
        }
        const originalPublishers = workDraftFromLocal.originalPublishers
        const currentOPid = currentOP.localId

        const administratorPublishers = currentOP.administratorPublishers
        const lastAPid = administratorPublishers.length
            ? Number(administratorPublishers[administratorPublishers.length - 1].localId)
            : 0

        const indexOfOP = originalPublishers.map((op: OriginalPublisherModel) => op.localId).indexOf(currentOPid)

        const newAdministratorPublisher: PublisherModel = {
            localId: lastAPid + 1,
            publisherName: publisher.publisherName,
            ipId: publisher.ipId,
            amend: true,
            ipiNumber: publisher.ipiNumber || null,
            lastUpdated: publisher.lastUpdated,
            hfaPublisherNumber: publisher.hfaPublisherNumber,
            publisherShare: 0,
        }

        const updatedCurrentOP: OriginalPublisherModel = {
            ...currentOP,
            publisherShare: 0,
            administratorPublishers: [...currentOP.administratorPublishers, newAdministratorPublisher],
        }

        const updatedOPArray = setItemToArrayByIndex(originalPublishers, +indexOfOP, updatedCurrentOP)

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            originalPublishers: updatedOPArray,
        }

        this.setWorkDraftToLocal(updatedWorkDraft)
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    get redirectingToClaimingTool(): boolean {
        return this._redirectingToClaimingTool
    }

    setRedirectingToClaimingTool(value: boolean): void {
        this._redirectingToClaimingTool = value
    }

    private setRedirectTrigger = (workAction: WorkAction, response: any) => {
        if (response.message) {
            this.rootStore.popupStore.setAlertData(response.message, true)
            return
        }

        switch (workAction) {
            case WorkAction.draftCreated:
                this.rootStore.popupStore.setAlertData(`${response.title} was added to drafts`, false)
                this.setWorkDraftToLocal(CLEAR_WORK_DRAFT)
                this.rootStore.router?.navigate(
                    Navigation.rightholder.registrationDrafts.root(
                        this.rootStore.rightsholderStore.currentRightsholderId
                    )
                )
                break
            case WorkAction.workCreated:
                this.rootStore.popupStore.setAlertData(`${response.title} was submitted successfully!`, false)
                this.rootStore.router?.navigate(
                    Navigation.rightholder.createWork.form.submission(
                        this.rootStore.rightsholderStore.currentRightsholderId
                    )
                )
                break
            case WorkAction.draftUpdated:
                this.rootStore.popupStore.setAlertData(`${response.title} draft has been updated`, false)
                this.setWorkDraftToLocal(CLEAR_WORK_DRAFT)
                this.rootStore.router?.navigate(
                    Navigation.rightholder.registrationDrafts.root(
                        this.rootStore.rightsholderStore.currentRightsholderId
                    )
                )
                break

            case WorkAction.editWorkCreated:
                this.rootStore.popupStore.setAlertData(`${response.title} was submitted successfully!`, false)
                this.rootStore.router?.navigate(
                    Navigation.rightholder.catalog.workEdit.submission(
                        this.rootStore.rightsholderStore.currentRightsholderId,
                        'work',
                        response.id
                    )
                )
                break
            case WorkAction.editDraftCreated:
                this.rootStore.popupStore.setAlertData(`${response.title} was submitted successfully!`, false)
                this.rootStore.router?.navigate(
                    Navigation.rightholder.catalog.workEdit.submission(
                        this.rootStore.rightsholderStore.currentRightsholderId,
                        'draft',
                        response.id
                    )
                )
                break
        }
    }
}
